'use client';

import { type MouseEvent, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Text,
  CopyIdButton,
  Scroll,
  Tooltip,
  MarkdownPreview,
  Badge,
  BadgeHttp,
  formatCategoryForDisplay,
} from '@terra/ui-design-system';
import { LinkSquare02Icon, Target03Icon } from '@terra/ui-icons';
import { token } from '@terra/ui-styled-system';
import type { SignalDto } from '@terra/shared-contracts-signals';
import type { SignalMarkableSectionParams } from '@terra/ui-design-system';
import type { SignalAssetTypeType } from '@terra/shared-schemas-orchestrator';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { isSignalBearingSurfaceAssetType } from '../../utils/signalSurfaceAssetNavigation';

type RejectedSignalDetailsViewProps = {
  signal: SignalDto;
  onNavigateToEndpoint: (endpointId: string, endpointUrl: string) => void;
  onEndpointContextMenu: (
    event: MouseEvent<HTMLElement>,
    endpointId: string,
    endpointUrl: string,
  ) => void;
  onNavigateToSurfaceAsset?: (
    assetId: string,
    assetType: SignalAssetTypeType,
  ) => void;
  onSurfaceAssetContextMenu?: (
    event: MouseEvent<HTMLElement>,
    assetId: string,
    assetType: SignalAssetTypeType,
  ) => void;
  surfaceAssetLabel?: string;
  renderMarkableSection?: (params: SignalMarkableSectionParams) => ReactNode;
};

export function RejectedSignalDetailsView({
  signal,
  onNavigateToEndpoint,
  onEndpointContextMenu,
  onNavigateToSurfaceAsset,
  onSurfaceAssetContextMenu,
  surfaceAssetLabel,
  renderMarkableSection,
}: RejectedSignalDetailsViewProps) {
  const t = useTranslations('designSystem.signals.drawer');
  const tSignals = useTranslations('signals');
  const dateFormatter = useDateFormatter();
  const endpointUrl = signal.endpoint?.url || '';
  const endpointMethod = signal.endpoint?.request?.method;
  const endpointIdForNav = signal.endpoint?.id;
  const graphqlName = signal.endpoint?.graphqlOperationName;
  const urlDisplay = graphqlName
    ? `${endpointUrl}?${graphqlName}`
    : endpointUrl;
  const surfaceAssetId = signal.assetId;
  const surfaceAssetType = isSignalBearingSurfaceAssetType(signal.assetType)
    ? signal.assetType
    : null;
  const showSurfaceAssetLink =
    !endpointUrl &&
    Boolean(surfaceAssetId) &&
    Boolean(surfaceAssetType) &&
    Boolean(onNavigateToSurfaceAsset);
  const surfaceAssetDisplayLabel =
    surfaceAssetLabel ||
    (surfaceAssetId && surfaceAssetType
      ? `${surfaceAssetType} (${surfaceAssetId})`
      : '');

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        <Box
          px="4"
          py="3"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.default"
        >
          <VStack gap="2" alignItems="stretch" w="full">
            <HStack gap="3" alignItems="center" w="full">
              <Target03Icon size={20} />
              <VStack alignItems="flex-start" gap="1" flex="1" minW="0">
                <Text fontSize="md" fontWeight="semibold" color="fg.default">
                  {tSignals('rejectedSignalDetailsTitle')}
                </Text>
                <Tooltip
                  title={signal.description ?? ''}
                  maxW="700px"
                  disabled={!signal.description}
                >
                  <Text
                    as="span"
                    fontSize="sm"
                    color="fg.muted"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxW="full"
                    cursor="default"
                  >
                    {signal.description ?? ''}
                  </Text>
                </Tooltip>
              </VStack>
              <HStack gap="1">
                <CopyIdButton id={signal.id} entityName={t('title')} />
              </HStack>
            </HStack>

            <HStack gap="2" alignItems="center" flexWrap="wrap">
              {signal.isAppWide === true && !signal.assetId && (
                <Tooltip title={t('appWideSignalTooltip')}>
                  <Badge
                    size="sm"
                    variant="emphasis"
                    bg={token('colors.crimson.3')}
                    borderColor={token('colors.crimson.6')}
                    color={token('colors.white')}
                  >
                    {t('appWideSignal')}
                  </Badge>
                </Tooltip>
              )}
              {signal.category && (
                <Badge size="sm" variant="outline">
                  {formatCategoryForDisplay(signal.category)}
                </Badge>
              )}
              <Badge size="sm" variant="fill" colorScheme="gray">
                {tSignals('rejectedStatusLabel')}
              </Badge>
            </HStack>
          </VStack>
        </Box>

        <Box flex="1" overflow="hidden" minH="0">
          <Scroll orientation="vertical" showScrollbar h="full">
            <Box p="4">
              <VStack gap="4" alignItems="stretch">
                {signal.analysis && (
                  <VStack gap="1" alignItems="flex-start">
                    <Text fontSize="sm" fontWeight="semibold">
                      {tSignals('analysisLabel')}
                    </Text>
                    {renderMarkableSection ? (
                      renderMarkableSection({
                        field: 'analysis',
                        fieldLabel: tSignals('analysisLabel'),
                        fullText: signal.analysis,
                        children: <MarkdownPreview source={signal.analysis} />,
                      })
                    ) : (
                      <MarkdownPreview source={signal.analysis} />
                    )}
                  </VStack>
                )}
                {signal.reason && (
                  <VStack gap="1" alignItems="flex-start">
                    <Text fontSize="sm" fontWeight="semibold">
                      {tSignals('confidenceReasonLabel')}
                    </Text>
                    {renderMarkableSection ? (
                      renderMarkableSection({
                        field: 'reason',
                        fieldLabel: tSignals('confidenceReasonLabel'),
                        fullText: signal.reason,
                        children: <MarkdownPreview source={signal.reason} />,
                      })
                    ) : (
                      <MarkdownPreview source={signal.reason} />
                    )}
                  </VStack>
                )}
                {endpointUrl && signal.isAppWide !== true && (
                  <HStack
                    gap="2"
                    alignItems="center"
                    p="2"
                    bg="bg.subtle"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.default"
                    cursor={endpointIdForNav ? 'pointer' : 'default'}
                    _hover={
                      endpointIdForNav
                        ? { bg: 'bg.muted', borderColor: 'border.emphasized' }
                        : undefined
                    }
                    onClick={() => {
                      if (endpointIdForNav) {
                        onNavigateToEndpoint(endpointIdForNav, endpointUrl);
                      }
                    }}
                    onContextMenu={(event) => {
                      if (!endpointIdForNav) return;
                      onEndpointContextMenu(
                        event,
                        endpointIdForNav,
                        endpointUrl,
                      );
                    }}
                  >
                    <LinkSquare02Icon
                      size={12}
                      color="var(--colors-fg-muted)"
                    />
                    {endpointMethod && (
                      <BadgeHttp method={endpointMethod} size="sm">
                        {endpointMethod}
                      </BadgeHttp>
                    )}
                    <Text fontSize="xs" color="fg.default" truncate flex="1">
                      {urlDisplay}
                    </Text>
                  </HStack>
                )}
                {showSurfaceAssetLink && surfaceAssetId && surfaceAssetType && (
                  <HStack
                    gap="2"
                    alignItems="center"
                    p="2"
                    bg="bg.subtle"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.default"
                    cursor="pointer"
                    _hover={{
                      bg: 'bg.muted',
                      borderColor: 'border.emphasized',
                    }}
                    onClick={() =>
                      onNavigateToSurfaceAsset?.(
                        surfaceAssetId,
                        surfaceAssetType,
                      )
                    }
                    onContextMenu={(event) =>
                      onSurfaceAssetContextMenu?.(
                        event,
                        surfaceAssetId,
                        surfaceAssetType,
                      )
                    }
                  >
                    <LinkSquare02Icon
                      size={12}
                      color="var(--colors-fg-muted)"
                    />
                    <Text fontSize="xs" color="fg.default" truncate flex="1">
                      {surfaceAssetDisplayLabel}
                    </Text>
                  </HStack>
                )}
                <Text fontSize="sm" color="fg.muted">
                  {dateFormatter.fromString(String(signal.createdAt))}
                </Text>
              </VStack>
            </Box>
          </Scroll>
        </Box>
      </VStack>
    </Box>
  );
}
